import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import { AXIS_THRESHOLDS, LABEL_THRESHOLDS, LEGEND_THRESHOLDS } from './ChartThresholds';

/**
 * Helper function to determine if a warning icon should be shown for a specific chart option
 * based on the chart's current dimensions in the dashboard.
 *
 * @param option The chart option being checked ('Legend', 'Label', 'Axis')
 * @param selectedChart The type of chart (e.g., 'multibar', 'pie')
 * @param graphDimension Current height and width of the chart
 * @param chartControl Current chart control properties from Redux
 * @returns boolean True if a warning should be shown
 */
export const shouldShowWarning = (
  option: string,
  selectedChart: string,
  graphDimension: { height: number; width: number } | null,
  chartControl: ChartControlsProps,
): boolean => {
  if (!graphDimension) return false;

  const h = Math.floor(graphDimension.height);
  const w = Math.floor(graphDimension.width);

  if (option === 'Legend') {
    if (!chartControl.legendOptions?.showLegend) return false;

    switch (selectedChart) {
      case 'multibar':
        return h <= LEGEND_THRESHOLDS.multibar.h;
      case 'waterfall':
        return h <= LEGEND_THRESHOLDS.waterfall.h;
      case 'stackedBar':
        return h <= LEGEND_THRESHOLDS.stackedBar.h;
      case 'horizontalBar':
      case 'horizontalStacked':
      case 'line':
      case 'area':
      case 'stackedArea':
      case 'stackedAreaChart':
      case 'scatterPlot':
      case 'boxplot':
      case 'boxPlot':
        return h <= LEGEND_THRESHOLDS.common.h;
      case 'pie':
        return h <= LEGEND_THRESHOLDS.pie.h || w <= LEGEND_THRESHOLDS.pie.w;
      case 'donut':
      case 'doughnut':
        return h <= LEGEND_THRESHOLDS.donut.h || w <= LEGEND_THRESHOLDS.donut.w;
      case 'rose':
        return h <= LEGEND_THRESHOLDS.rose.h || w <= LEGEND_THRESHOLDS.rose.w;
      case 'calendar':
        return h <= LEGEND_THRESHOLDS.calendar.h;
      case 'heatmap':
        return h <= LEGEND_THRESHOLDS.heatmap.h;
      case 'funnel':
        return h <= LEGEND_THRESHOLDS.funnel.h;
      case 'treemap':
      case 'treeMap':
        return h <= LEGEND_THRESHOLDS.treemap.h; // Breadcrumb threshold
      default:
        return h <= LEGEND_THRESHOLDS.common.h;
    }
  }

  if (option === 'Label') {
    if (!chartControl.labelOptions?.showLabel) return false;

    switch (selectedChart) {
      case 'pie':
      case 'donut':
      case 'doughnut':
      case 'rose':
        return h <= LABEL_THRESHOLDS.pieArea.h || w <= LABEL_THRESHOLDS.pieArea.w;
      case 'funnel':
        return h <= LABEL_THRESHOLDS.funnel.h || w <= LABEL_THRESHOLDS.funnel.w;

      case 'calendar':
      case 'heatmap':
      case 'treemap':
      case 'treeMap':
      case 'simplecard':
      case 'gauge':
        return false;
      default:
        // Most Cartesian charts hide labels around 150px height
        return h <= LABEL_THRESHOLDS.common.h;
    }
  }

  if (option === 'Axis') {
    const showX = Object.values(chartControl.axisOptions?.xAxis || {}).some(
      (ax: any) => ax.position !== 'hide',
    );
    const showY = Object.values(chartControl.axisOptions?.yAxis || {}).some(
      (ax: any) => ax.position !== 'hide',
    );

    if (!showX && !showY) return false;

    // Most Cartesian charts have tight axes below 150px
    const cartesianCharts = [
      'multibar',
      'stackedBar',
      'horizontalBar',
      'horizontalStacked',
      'line',
      'area',
      'stackedArea',
      'stackedAreaChart',
      'scatterPlot',
      'boxplot',
      'boxPlot',
      'heatmap',
      'waterfall',
    ];

    if (cartesianCharts.includes(selectedChart)) {
      return h <= AXIS_THRESHOLDS.common.h;
    }

    if (selectedChart === 'gauge') {
      return Math.min(h, w) < AXIS_THRESHOLDS.gauge.min;
    }
  }

  if (option === 'Visual Scale') {
    if (!chartControl.geoChartControls?.showVisualScale) return false;

    if (selectedChart === 'filledMap' || selectedChart === 'bubbleMap') {
      return h <= LEGEND_THRESHOLDS.common.h || w <= LEGEND_THRESHOLDS.common.w;
    }
  }

  return false;
};
